import { rawDynoapeAPI, dynoapeAPI } from '../dynoape';
import { PATHS } from '../config';
import type { EmployeeWage, EmployeeWagesRequest } from './Models';

const getEmployeeWages = async (
    departmentId: string,
): Promise<EmployeeWage[]> => {
    const response = await rawDynoapeAPI.get(
        PATHS.employeeFinances(departmentId),
    );
    return response.data.body.elements;
};

const postEmployeeWage = async (
    departmentId: string,
    employeeId: string,
    value: number,
): Promise<EmployeeWage> =>
    await dynoapeAPI.post(PATHS.employeeFinance(departmentId, employeeId), {
        value,
    });

const putEmployeeWage = async (
    departmentId: string,
    employeeId: string,
    wageId: string,
    value: number,
): Promise<EmployeeWage> =>
    await dynoapeAPI.put(PATHS.employeeWage(departmentId, employeeId, wageId), {
        value,
    });

/*
 * The endpoint responds with an empty body on success — `?? true` marks
 * it (honest: delete throws on failure, dynoapeAPI.delete has no internal
 * catch, so undefined can only mean an empty-body success here).
 */
const deleteEmployeeWage = async (
    departmentId: string,
    employeeId: string,
    wageId: string,
): Promise<EmployeeWage | true> =>
    (await dynoapeAPI.delete(
        PATHS.employeeWage(departmentId, employeeId, wageId),
    )) ?? true;

/*
 * Saves one wage and resolves an unambiguous success marker for the
 * composite below:
 * - delete (wage === undefined): resolves `true` on success, throws on
 *   failure;
 * - post/put: the endpoint returns the wage on success; a failed save
 *   resolves undefined (dynoapeAPI's post/put swallow HTTP errors — the
 *   axios interceptor has already shown the error toast).
 * undefined therefore means "this wage was not saved".
 */
const saveEmployeeWage = async (
    departmentId: string,
    employeeId: string,
    wage: number | undefined,
    wageId?: string,
): Promise<EmployeeWage | true | undefined> => {
    if (wageId !== undefined) {
        return wage === undefined
            ? deleteEmployeeWage(departmentId, employeeId, wageId)
            : putEmployeeWage(departmentId, employeeId, wageId, wage);
    }

    if (wage === undefined) {
        throw new Error('New wage cannot be undefined');
    }

    return postEmployeeWage(departmentId, employeeId, wage);
};

export const saveEmployeeWages = async ({
    departmentId,
    employeeWages,
    setSavingProgress,
}: EmployeeWagesRequest): Promise<void> => {
    const total = employeeWages.length;
    let completed = 0;
    const failedSavesEmployeeIds = new Set<string>();

    for (const { employeeId, wage, wageId } of employeeWages) {
        try {
            const savedWage = await saveEmployeeWage(
                departmentId,
                employeeId,
                wage,
                wageId,
            );

            // Failed post/put saves resolve undefined (the wrapper
            // swallows HTTP errors); failed deletes throw and land in
            // the catch. Both count as failed saves for the grid's
            // failed-row highlighting.
            if (savedWage === undefined) {
                failedSavesEmployeeIds.add(employeeId);
            }
        } catch {
            failedSavesEmployeeIds.add(employeeId);
        } finally {
            completed += 1;
            setSavingProgress?.({
                completed,
                total,
                failedSavesEmployeeIds,
            });
        }
    }
};

const EmployeeFinanceService = {
    getEmployeeWages,
    postEmployeeWage,
    putEmployeeWage,
    saveEmployeeWages,
};

export default EmployeeFinanceService;
